import { alpha } from '@mui/material/styles';
import { outlinedInputClasses } from '@mui/material/OutlinedInput';
import { gray, brand } from '../../getDesignTokens';

/**
 * The outlined-input trio — read as one concept because they always
 * render together for a TextField:
 *
 *   - MuiInputBase       — the underlying wrapper (border reset)
 *   - MuiOutlinedInput   — the actual bordered container (padding,
 *                          border color states, focus ring)
 *   - MuiInputAdornment  — icons / prefixes / suffixes inside the input
 *
 * MUI's default `notchedOutline` (the fieldset + legend that "notches"
 * around the floating label) is disabled here — the label sits on top
 * of the input via MuiInputLabel's pill-cutout trick instead. See
 * Label.jsx for that side of the treatment.
 */
export const textInputCustomizations = {
  MuiInputBase: {
    styleOverrides: {
      root: {
        border: 'none',
      },
      input: {
        '&::placeholder': {
          opacity: 0.7,
          color: gray[500],
        },
      },
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      input: {
        padding: 0,
      },
      inputMultiline: {
        padding: 0,
        lineHeight: 1.5,
        minHeight: '1.5rem',
      },
      root: ({ theme }) => ({
        padding: '8px 12px',
        color: theme.palette.text.primary,
        borderRadius: theme.shape.borderRadius,
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.default,
        transition: 'border 120ms ease-in',
        // MUI 9 emits MuiInputBase-multiline, not MuiOutlinedInput-multiline —
        // outlinedInputClasses.multiline resolves to the InputBase name. The
        // two size variants below collapsed into this one rule: there is no
        // MuiInputBase-sizeMedium class at all (medium is the default and
        // emits nothing), so that selector never matched in any version, and
        // sizeSmall only repeated the same height: auto.
        '&.MuiInputBase-multiline': {
          height: 'auto',
          alignItems: 'flex-start',
        },
        '&:hover': {
          borderColor: gray[400],
        },
        [`&.${outlinedInputClasses.focused}`]: {
          boxShadow: `0 0 0 3px ${alpha(brand[500], 0.35)}`,
          borderColor: brand[400],
        },
        ...theme.applyStyles('dark', {
          '&:hover': {
            borderColor: gray[500],
          },
        }),
        variants: [
          {
            props: {
              size: 'small',
            },
            style: {
              minHeight: '2.25rem',
            },
          },
          {
            props: {
              size: 'medium',
            },
            style: {
              minHeight: '2.5rem',
            },
          },
        ],
      }),
      notchedOutline: {
        border: 'none',
      },
    },
  },
  MuiInputAdornment: {
    styleOverrides: {
      root: ({ theme }) => ({
        color: theme.palette.grey[500],
        ...theme.applyStyles('dark', {
          color: theme.palette.grey[400],
        }),
      }),
    },
  },
};
